import React, { useMemo, useState } from 'react';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../../components/bootstrap/Modal';
import Button from '../../../components/bootstrap/Button';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Select from '../../../components/bootstrap/forms/Select';
import Checks, { ChecksGroup } from '../../../components/bootstrap/forms/Checks';
import { useFormik } from 'formik';
import Input from '../../../components/bootstrap/forms/Input';
import Icon from '../../../components/icon/Icon';
import Label from '../../../components/bootstrap/forms/Label';
import { formatCurrency } from 'pages/comptabilite/utils/functions';

export default function ValidationPaiement({
	setAmount,
	setpaiementPopup,
	amount,      // total amount (EUR in UI)
	orderRef,         // orderId
	email,       // customer email
	onFormToken, // optional callback
}) {
	const percentList = useMemo(() => [30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95], []);
	const [montantPercent, setMontantPercent] = useState(30);
	const [loading, setLoading] = useState(false);
	const [apiErr, setApiErr] = useState(null);
	const [formToken, setFormToken] = useState(null);

	const minAmount = useMemo(() => (Number(amount || 0) * 30) / 100, [amount]);

	const formik = useFormik({
		initialValues: { accompte: 'normal', amount: Number(amount || 0) },
		enableReinitialize: true,
		onSubmit: () => { },
	});

	const handleSelectAccompte = (type) => {
		formik.setFieldValue('accompte', type);
		if (type === 'normal') formik.setFieldValue('amount', Number(amount || 0));
		if (type === 'pourcentage') formik.setFieldValue('amount', (Number(amount || 0) * Number(montantPercent)) / 100);
		if (type === 'montant') formik.setFieldValue('amount', Math.max(minAmount, Math.min(Number(amount || 0), Number(formik.values.amount || 0))));
	};

	const createPayment = async () => {
		setApiErr(null);
		setLoading(true);

		try {
			const selected = Math.round(Number(formik.values.amount));
			if (!selected) throw new Error("Amount is required");

			// send to parent (optional)
			if (typeof setAmount === 'function') setAmount(selected);

			const resp = await fetch("/coreService/api/systempay/create-payment", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					amount: selected,   // euros in UI -> backend converts to cents
					currency: "EUR",
					orderId: orderRef,
					email,
				}),
			});

			const data = await resp.json();
			if (!resp.ok) throw new Error(data?.message || "CreatePayment failed");

			setFormToken(data.formToken);
			if (typeof onFormToken === "function") onFormToken(data.formToken);
				setpaiementPopup(false);
			
			// Keep modal open to show the payment form
		} catch (e) {
			setApiErr(String(e?.message || e));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal isOpen={true} setIsOpen={() => { }} isCentered size={'sm'} classNameMd='nv-3'>
			<ModalHeader>
				<div className='w-100 text-info text-center d-flex justify-content-center'>
					<Icon icon='CreditCard' size={'2x'} />
					<ModalTitle className='fs-4 fw-semibold'> Paiement</ModalTitle>
				</div>
			</ModalHeader>

			<ModalBody className='px-4'>
				<div className='fs-5 text-center'>
					Le montant total est <strong>{formatCurrency(amount, 'EUR')}</strong>
				</div>

				{apiErr && <div className="text-danger mt-3">{apiErr}</div>}


				<div className='mt-4 mb-2'>Vous avez la possibilité de payer :</div>

				<div className='row g-4'>
					<ChecksGroup>
						<Checks
							type='radio'
							id='normal'
							label='La totalité du montant.'
							name='accompte'
							value='normal'
							onChange={() => handleSelectAccompte('normal')}
							checked={formik.values.accompte === 'normal'}
						/>
						<Checks
							type='radio'
							id='pourcentage'
							label='Un acompte %.'
							name='accompte'
							value='pourcentage'
							onChange={() => handleSelectAccompte('pourcentage')}
							checked={formik.values.accompte === 'pourcentage'}
						/>
						<Checks
							type='radio'
							id='montant'
							label='Un acompte spécifique.'
							name='accompte'
							value='montant'
							onChange={() => handleSelectAccompte('montant')}
							checked={formik.values.accompte === 'montant'}
						/>
					</ChecksGroup>

					{formik.values.accompte === 'pourcentage' && (
						<div className='row mt-4 d-flex'>
							<FormGroup label='Acompte (%)' className='col-6'>
								<Select
									id='percent'
									ariaLabel='pourcentage'
									placeholder='pourcentage'
									list={percentList.map((p) => ({ value: p, text: `${p}%` }))}
									value={montantPercent}
									onChange={(e) => {
										const p = Number(e.target.value);
										setMontantPercent(p);
										formik.setFieldValue('amount', (Number(amount || 0) * p) / 100);
									}}
								/>
							</FormGroup>

							<div className='col-6 text-center'>
								<Label>Montant (€)</Label>
								<div className='fw-semibold'>{formatCurrency(formik.values.amount, 'EUR')}</div>
							</div>
						</div>
					)}

					{formik.values.accompte === 'montant' && (
						<FormGroup id='amount' label='Acompte'>
							<small className='text-muted'> (minimum 30% {formatCurrency(minAmount, 'EUR')})</small>
							<Input
								name='amount'
								type='number'
								min={minAmount}
								max={Number(amount || 0)}
								autoComplete='off'
								value={formik.values.amount}
								onChange={(e) => formik.setFieldValue('amount', e.target.value === '' ? '' : Number(e.target.value))}
							/>
						</FormGroup>
					)}

					<div className='col-12'>
						<Button
							color='info'
							className='w-100'
							isDisable={!formik.values.amount || loading}
							onClick={createPayment}
						>
							{loading ? "Création du paiement..." : "Enregistrer"}
						</Button>

						<Button
							color='light'
							className='w-100 mt-2'
							onClick={() => setpaiementPopup(false)}
						>
							Annuler
						</Button>
					</div>
				</div>


			</ModalBody>
		</Modal >
	);
}
